import { useState } from "react";
import { DataTable } from "@/components/data-table";
import { Form, useForm, SchemaFormHook } from "@/components/form";
import { formatString } from "@/shared/formats";
import {
    NaturalezaReceptorEnum,
    TipoContribuyenteEnum,
    TipoDocumentoIdentidadEnum,
    TipoOperacionEnum
} from "@/shared/types/contribuyente";
import {
    Box,
    Chip,
    IconButton,
    Stack,
    StackProps,
    Tab,
    Tabs,
    Tooltip
} from "@mui/material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import StorageIcon from '@mui/icons-material/Storage';
import PublicIcon from '@mui/icons-material/Storage';
import { useFilters } from "@/components/filters";
import TurucContribuyenteSearch from "./turuc-contribuyente-search";
import { TurucContribuyente as TurucCliente, getPaisByCodigo } from "../api/search-turuc";
import { notify, notifyError } from "@/shared/notification";
import { Dialog, ModalHeader, useOpenModal } from "@/components/modal";
import { DataFetcher } from "@/components/display";
import { Cliente, ClienteRequest } from "@/shared/types/cliente";
import { useAddClienteMutation } from "@/features/auxiliar/clientes/api/add-cliente";
import { useGetAllClientesQuery } from "@/features/auxiliar/clientes/api/get-all-clientes";
import { checkCliente } from "@/features/persona/clientes/api/check-cliente";
import { EditClienteMutation } from "@/features/auxiliar/clientes/api/edit-cliente";
import { GetClienteQuery } from "@/features/auxiliar/clientes/api/get-cliente";
import { addClienteFormSchema } from "@/features/auxiliar/clientes/components/add-cliente-form.schema";
import EditClienteForm from "@/features/auxiliar/clientes/components/edit-cliente-form";
import AddClienteForm from "@/features/auxiliar/clientes/components/add-cliente-form";
import { useOperationalMode } from "@/features/configuration/hooks/use-operational-mode";

interface ClienteSearchProps extends StackProps {
    onClienteSelect?: (cliente: Cliente) => void;
    selectedClienteId?: string | null;
}

export default function ClienteManualSearch({
    onClienteSelect,
    selectedClienteId,
    ...props
}: ClienteSearchProps) {
    const [tabValue, setTabValue] = useState(0);
    const [creatingRuc, setCreatingRuc] = useState<string | null>(null);
    const [editingClienteId, setEditingClienteId] = useState<string | null>(null);
    const [preloadedClienteData, setPreloadedClienteData] = useState<ClienteRequest | null>(null);
    const openModal = useOpenModal();

    const filterData = useFilters({
        useURL: false,
        filters: [
            { type: "string", label: "Razón Social", name: "razonSocial" },
            { type: "string", label: "RUC", name: "ruc" },
            { type: "string", label: "Email", name: "email" },
            { type: "pagination" }
        ]
    });

    const queryResult = useGetAllClientesQuery(filterData.filterData);

    const filterForm = useForm({
        defaultValues: filterData.filterData,
        onSubmit: ({ value }) => filterData.setFilterData(value),
        closeModalOnSubmit: false,
        submitOnce: false,
    });

    /*
      cuando se selecciona contribuyente de DNIT (TURUC la api):
      1. Verifica si ya existe en BD local por RUC
      2. Si existe, usa el existente
      3. Si no existe, abre el modal de agregar cliente con datos precargados
     */
    const handleDnitSelect = async (turucCliente: TurucCliente) => {
        const rucParts = turucCliente.ruc.split("-");
        const ruc = rucParts[0];
        const dv = rucParts[1] || "";

        // Validate required data from TuRuc
        if (!ruc || !turucCliente.razonSocial) {
            notifyError("Datos incompletos del contribuyente de DNIT");
            return;
        }

        setCreatingRuc(turucCliente.ruc);
        try {
            // Verificar si ya existe en la BD local
            const existente = await checkCliente(ruc);

            if (existente) {
                // Ya existe, usar el existente
                notify(`Cliente ${existente.nombre} seleccionado`);
                onClienteSelect?.(existente);
                return;
            }

            // Obtener ID de Paraguay
            const paraguay = await getPaisByCodigo("PRY");
            if (!paraguay) {
                notifyError("No se encontró el país Paraguay en la configuración");
                return;
            }

            // No existe, preparar datos precargados y abrir modal de agregar
            const preloadedData: ClienteRequest = {
                ruc: ruc,
                dv: dv,
                numeroDocumento: ruc,
                razonSocial: turucCliente.razonSocial.trim(),
                naturaleza: NaturalezaReceptorEnum.Contribuyente,
                tipoOperacion: turucCliente.esEntidadPublica
                    ? TipoOperacionEnum.B2G
                    : turucCliente.esPersonaJuridica ? TipoOperacionEnum.B2B : TipoOperacionEnum.B2C,
                tipoDocumento: TipoDocumentoIdentidadEnum.CedulaParaguaya,
                tipoContribuyente: turucCliente.esPersonaJuridica
                    ? TipoContribuyenteEnum.PersonaJuridica
                    : TipoContribuyenteEnum.PersonaFisica,
                codigoPais: "PRY",
                paisId: paraguay.id,
                numeroCasa: 0,
            };

            setPreloadedClienteData(preloadedData);
            openModal("addClienteFromDnit");
        } catch (error: any) {
            console.error("Error al procesar cliente de DNIT:", error);
            notifyError(error);
        } finally {
            setCreatingRuc(null);
        }
    };

    return (
        <Stack minHeight="70vh" px={3} pb={3} {...props}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mx: -3 }}>
                <Tabs
                    value={tabValue}
                    onChange={(_, newValue) => setTabValue(newValue)}
                    aria-label="Búsqueda de contribuyentes"
                    sx={{ px: 3 }}
                >
                    <Tab
                        icon={<StorageIcon />}
                        iconPosition="start"
                        label="Contribuyentes Locales"
                    />
                    <Tab
                        icon={<PublicIcon />}
                        iconPosition="start"
                        label="Buscar en DNIT"
                    />
                </Tabs>
            </Box>

            {tabValue === 0 && (
                <Stack flex={1} gap={2} pt={2}>
                    <Form form={filterForm} direction="row" flexWrap="wrap" gap={2}>
                        <Form.TextField name="razonSocial" label="Razón Social" size="small" />
                        <Form.TextField name="ruc" label="RUC" size="small" />
                        <Form.TextField name="email" label="Email" size="small" />
                        <Form.SubmitButton>Filtrar</Form.SubmitButton>
                    </Form>
                    <DataTable flex={1}>
                        <DataTable.Content
                            fit="flex"
                            rows={queryResult.data?.result}
                            loading={queryResult.isFetching}
                            columns={[
                                {
                                    key: "razonSocial",
                                    header: "Razón Social",
                                    render: row => {
                                        const isSelected = selectedClienteId && row.id === selectedClienteId;
                                        return (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    py: 0.5,
                                                    px: 1,
                                                    mx: -1,
                                                    bgcolor: isSelected ? 'primary.light' : 'transparent',
                                                    borderRadius: 1,
                                                }}
                                            >
                                                {isSelected && <CheckCircleIcon fontSize="small" sx={{ color: 'text.primary' }} />}
                                                {formatString(row.nombre)}
                                            </Box>
                                        );
                                    }
                                },
                                { key: "ruc", header: "RUC", render: row => formatString(row.ruc) },
                                { key: "dv", header: "DV", render: row => formatString(row.dv) },
                                { key: "numDocumento", header: "Numero Documento", render: row => formatString(row.numDocumento) },
                                {
                                    key: "tipo",
                                    header: "Tipo",
                                    width: "140px",
                                    render: row => (
                                        <Chip
                                            label={row.tipoContribuyente === TipoContribuyenteEnum.PersonaJuridica ? "Empresa" : "Persona Física"}
                                            size="small"
                                            variant="outlined"
                                            color={row.tipoContribuyente === TipoContribuyenteEnum.PersonaJuridica ? "primary" : "default"}
                                        />
                                    )
                                },
                                { key: "email", header: "Email", render: row => formatString(row.email) },
                                {
                                    header: "", align: "center", width: "auto",
                                    render: a => (
                                        <Stack direction="row" gap={0.5}>
                                            <Tooltip title="Editar Cliente">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setEditingClienteId(a.id);
                                                        openModal("editCliente", a.id);
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Seleccionar cliente">
                                                <IconButton size="small" onClick={() => onClienteSelect?.(a)}>
                                                    <AddIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    )
                                }
                            ]}
                        />
                        <DataTable.Pagination queryResult={queryResult} filterData={filterData} />
                    </DataTable>
                </Stack>
            )}

            {tabValue === 1 && (
                <TurucContribuyenteSearch
                    onContribuyenteSelect={handleDnitSelect}
                    creatingRuc={creatingRuc}
                    flex={1}
                />
            )}

            {editingClienteId && (
                <EditClienteDialog
                    clienteId={editingClienteId}
                    onClose={() => setEditingClienteId(null)}
                    onSuccess={(cliente) => {
                        setEditingClienteId(null);
                        onClienteSelect?.(cliente);
                    }}
                />
            )}

            {preloadedClienteData && (
                <AddClienteFromDnitDialog
                    preloadedData={preloadedClienteData}
                    onClose={() => setPreloadedClienteData(null)}
                    onSuccess={(cliente) => {
                        setPreloadedClienteData(null);
                        onClienteSelect?.(cliente);
                    }}
                />
            )}
        </Stack>
    );
}

function EditClienteDialog({
    clienteId,
    onClose,
    onSuccess
}: {
    clienteId: string;
    onClose: () => void;
    onSuccess: (cliente: Cliente) => void;
}) {
    const { facturacionEnabled } = useOperationalMode();

    return (
        <Dialog code="editCliente" size="md" onClose={onClose}>
            <ModalHeader>Editar Cliente</ModalHeader>
            <GetClienteQuery id={clienteId}>
                {query => <DataFetcher flex={1} query={query}>
                    {cliente => <EditClienteMutation id={cliente.id} isFe={facturacionEnabled}>
                        {mutation => <SchemaFormHook
                            schema={addClienteFormSchema}
                            onSubmit={async ({ value }) => {
                                const result = await mutation.mutateAsync(value);
                                notify("El cliente se ha editado correctamente.");
                                onSuccess(result);
                            }}
                            defaultValues={{
                                ...cliente,
                                id: cliente.id,
                                razonSocial: cliente.nombre,
                                numeroDocumento: cliente.numDocumento,
                                ciudadId: cliente.ciudad?.id ?? "",
                                ciudadSeleccionada: cliente.ciudad?.nombre,
                                departamentoId: cliente.departamento?.id ?? "",
                                departamentoSeleccionado: cliente.departamento?.nombre,
                                paisId: cliente?.pais?.id,
                                codigoPais: cliente.codigoPais,
                                email: cliente.email || "",
                            }}
                            closeModalOnSubmit
                            submitOnce
                        >
                            {form => <EditClienteForm form={form} padding={3} />}
                        </SchemaFormHook>}
                    </EditClienteMutation>}
                </DataFetcher>}
            </GetClienteQuery>
        </Dialog>
    );
}

function AddClienteFromDnitDialog({
    preloadedData,
    onClose,
    onSuccess
}: {
    preloadedData: ClienteRequest;
    onClose: () => void;
    onSuccess: (cliente: Cliente) => void;
}) {
    const addMutation = useAddClienteMutation();

    return (
        <Dialog code="addClienteFromDnit" size="md" onClose={onClose}>
            <ModalHeader>Agregar Cliente (DNIT)</ModalHeader>
            <SchemaFormHook
                schema={addClienteFormSchema}
                onSubmit={async ({ value }) => {
                    const result = await addMutation.mutateAsync(value);
                    notify("El cliente se ha creado correctamente.");
                    onSuccess(result);
                }}
                defaultValues={{
                    razonSocial: preloadedData.razonSocial,
                    numeroDocumento: preloadedData.numeroDocumento || "",
                    ruc: preloadedData.ruc || "",
                    dv: preloadedData.dv || "",
                    email: "",
                    naturaleza: preloadedData.naturaleza,
                    tipoOperacion: preloadedData.tipoOperacion,
                    tipoDocumento: preloadedData.tipoDocumento,
                    tipoContribuyente: preloadedData.tipoContribuyente,
                    codigoPais: preloadedData.codigoPais,
                    paisId: preloadedData.paisId,
                    numeroCasa: preloadedData.numeroCasa ?? 0,
                    direccion: "Sin Dirección",
                    departamentoId: "",
                    ciudadId: "",
                    departamentoSeleccionado: "",
                    ciudadSeleccionada: "",
                }}
                closeModalOnSubmit
                submitOnce
            >
                {form => <AddClienteForm form={form} padding={3} />}
            </SchemaFormHook>
        </Dialog>
    );
}

